import { Dispatch, RefObject, SetStateAction } from "react";
import { Loader } from "shared/components/Loader";
import { Hide, Show } from "shared/components";
import { QueryClient, UseQueryResult } from "@tanstack/react-query";
import { CheckoutSlice } from "../../slices/Checkout";
import { AX_ORIGIN, SubmissionDisclosureSubStep } from "../../constants";
import {
  Application,
  Verification,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { MoveInDateSlice } from "../../slices/MoveInDate";
import { SubmissionDisclosure } from "../../slices/SubmissionDisclosure";
import { CombinedIncome } from "../../slices/CombinedIncome";
import { Step } from "../../types";

interface StepContentProps {
  activeStepName: string | null;
  iframeRef: RefObject<HTMLIFrameElement>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  setOpen: (open: boolean) => void;
  setActiveStepName: (stepName: string) => void;
  stepNames: string[];
  allSteps: Record<string, Step>;
  isApplicationRefreshing: boolean;
  refetchApplication: () => Promise<unknown>;
  application: Application;
  applicant: Application["applicants"][number];
  hasIncomeSource: boolean;
  verificationsMeta: UseQueryResult<
    {
      verifications: Verification[];
    },
    Error
  >;
  queryClient: QueryClient;
  submissionDisclosureSubStep: SubmissionDisclosureSubStep;
  setSubmissionDisclosureSubStep: Dispatch<
    SetStateAction<SubmissionDisclosureSubStep>
  >;
}

export const StepContent = ({
  activeStepName,
  iframeRef,
  loading,
  setLoading,
  setOpen,
  setActiveStepName,
  stepNames,
  allSteps,
  isApplicationRefreshing,
  refetchApplication,
  application,
  applicant,
  hasIncomeSource,
  verificationsMeta,
  queryClient,
  submissionDisclosureSubStep,
  setSubmissionDisclosureSubStep
}: StepContentProps) => {
  const getNextStepIndex = (
    stepNames: string[],
    currentStep: string
  ): number => {
    const currentIdx = stepNames.indexOf(currentStep);
    return currentIdx + 1;
  };

  const getPreviousStepIndex = (
    stepNames: string[],
    currentStep: string
  ): number => {
    const currentIdx = stepNames.indexOf(currentStep);
    return currentIdx - 1;
  };

  const incomeVerificationStep = allSteps["income_verification"];
  const incomeVerificationData = incomeVerificationStep?.data;
  const shouldSkipVerifiedIncomeStep =
    incomeVerificationData?.do_you_have_source_of_income !== "Yes";

  const handleSubmissionDisclosureNextStep = () => {
    verificationsMeta.refetch();
    const nextIdx = getNextStepIndex(stepNames, "submission_disclosure_step");
    if (nextIdx >= 0) setActiveStepName(stepNames[nextIdx]);
  };

  const handleCombinedIncomeNextStep = () => {
    verificationsMeta.refetch();
    const nextIdx = getNextStepIndex(stepNames, "combined_income");
    if (nextIdx >= 0) setActiveStepName(stepNames[nextIdx]);
  };

  const handleMoveInDateSuccess = () => {
    const nextIdx = getNextStepIndex(stepNames, "move_in_date");
    refetchApplication().then(() => {
      if (nextIdx >= 0) setActiveStepName(stepNames[nextIdx]);
    });
  };

  const handleCheckoutSuccess = () => {
    setOpen(false);
    queryClient.invalidateQueries({
      queryKey: ["APPLICATION", String(application.id)]
    });
  };

  const handleCheckoutGoBack = () => {
    const nextIdx = getPreviousStepIndex(stepNames, activeStepName!);

    if (
      stepNames[nextIdx] === "verified_income" &&
      shouldSkipVerifiedIncomeStep
    )
      setActiveStepName(stepNames[nextIdx]);
    else if (stepNames[nextIdx] === "verified_income")
      setActiveStepName(stepNames[nextIdx - 1]);
    else if (nextIdx >= 0) setActiveStepName(stepNames[nextIdx]);
  };

  const submissionDisclosureVerification =
    verificationsMeta.data?.verifications.find(
      (verification) =>
        verification.name === VerificationNames.SubmissionDisclosure
    );

  const combinedIncomeVerification = verificationsMeta.data?.verifications.find(
    (verification) => verification.name === VerificationNames.CombinedIncome
  );

  const shouldShowLoading =
    isApplicationRefreshing ||
    (activeStepName === "submission_disclosure_step" &&
    submissionDisclosureSubStep !== SubmissionDisclosureSubStep.ReviewAndSign
      ? false
      : loading &&
        activeStepName !== "checkout" &&
        activeStepName !== "move_in_date" &&
        activeStepName !== "combined_income");

  return (
    <div className="relative bg-background-900">
      <Show
        if={
          activeStepName === "submission_disclosure_step" &&
          submissionDisclosureSubStep !==
            SubmissionDisclosureSubStep.ReviewAndSign
        }
      >
        <SubmissionDisclosure
          refetchApplication={refetchApplication}
          application={application}
          applicant={applicant}
          verification={submissionDisclosureVerification}
          setSubStep={setSubmissionDisclosureSubStep}
          subStep={submissionDisclosureSubStep}
          onNextStep={handleSubmissionDisclosureNextStep}
        />
      </Show>

      <Show if={activeStepName === "move_in_date"}>
        <MoveInDateSlice
          onSuccess={handleMoveInDateSuccess}
          applicationId={Number(application.id)}
          moveInDate={application.desired_move_in_date}
          availableAt={application.unit?.available_at}
        />
      </Show>

      <Show if={activeStepName === "combined_income"}>
        <CombinedIncome
          application={application}
          applicant={applicant}
          refetchApplication={refetchApplication}
          verification={combinedIncomeVerification}
          refetchVerification={verificationsMeta.refetch}
          onNextStep={handleCombinedIncomeNextStep}
        />
      </Show>

      <Show if={activeStepName === "checkout"}>
        <CheckoutSlice
          onSuccess={handleCheckoutSuccess}
          goBack={handleCheckoutGoBack}
          applicant={applicant}
          steps={allSteps}
          setActiveStepName={setActiveStepName}
          applicantId={Number(applicant.id)}
          hasIncomeSource={hasIncomeSource}
          sendDisclosureEmail={
            applicant.preferred_submission_disclosure_type == "send_by_email"
          }
          isSubmissionDisclosureFinished={
            submissionDisclosureVerification?.status ===
            VerificationStatus.Finished
          }
          application={application}
        />
      </Show>

      <Hide
        if={
          activeStepName === "move_in_date" ||
          activeStepName === "checkout" ||
          activeStepName === "combined_income" ||
          (activeStepName === "submission_disclosure_step" &&
            submissionDisclosureSubStep !==
              SubmissionDisclosureSubStep.ReviewAndSign)
        }
      >
        <iframe
          ref={iframeRef}
          key={activeStepName}
          onLoad={() => setLoading(false)}
          src={`${AX_ORIGIN}/manual-applications`}
          className="w-full h-[calc(80vh-72px)] overflow-hidden animate-appear bg-background-900 rounded-br-lg"
        />
      </Hide>

      <Show if={shouldShowLoading}>
        <div className="absolute inset-0 flex items-center justify-center animate-appear">
          <Loader />
        </div>
      </Show>
    </div>
  );
};
